/**
 * FinanceOverview — FM1 Staff Finance Overview Page
 *
 * Route: /finance/overview
 * RBAC: finance.kpi.view (cfo, finance_manager, institution_admin, super_admin)
 *       finance.account.view.any (registrar, finance_officer, etc.) — shows nav tiles
 *
 * Canonical surfaces:
 *   FK1 — Institutional Finance KPI (frozen, GET /api/finance/kpi/institutional)
 *   FK2 — Per-Program Finance KPI (BLOCKED: enrollment authority absent)
 *
 * FM0-D1 §3.1 Nav: Overview is the first Finance nav item.
 * FK1 contract is FROZEN — this page renders it but does NOT modify it.
 */
import React, { useEffect, useState } from "react";
import {
  TrendingUp, Wallet, BarChart2, Users, ArrowRight,
  RefreshCw, AlertCircle, Lock
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MoneyAmount, MoneyBadge } from "../components/finance/MoneyAmount";
import { FinanceCapabilityState } from "../components/finance/FinanceCapabilityState";
import { PageHeader } from "../components/layout/Shell";
// Permissions that can see KPI (mirrors finance_rbac.py ROLE_PERMISSIONS)
const KPI_ROLES = new Set([
  "cfo", "finance_manager", "institution_admin", "super_admin",
  "registrar", "programme_manager", "hod", "dean", "principal",
]);

export default function FinanceOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canViewKpi = KPI_ROLES.has(user?.role);

  useEffect(() => {
    if (!canViewKpi) return;
    setLoading(true);
    setError(null);
    api
      .get("/finance/kpi/institutional")
      .then((r) => setKpi(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load KPI"))
      .finally(() => setLoading(false));
  }, [canViewKpi]);

  return (
    <div className="space-y-6" data-testid="finance-overview-page">
      <PageHeader title="Finance Overview" description="Institutional financial position and capability registry." />

      {/* FK1 KPI Section */}
      <section data-testid="finance-fk1-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-400 tracking-wider uppercase">
            Institutional Finance KPI (FK1)
          </h2>
          {loading && (
            <RefreshCw className="h-4 w-4 text-slate-500 animate-spin" />
          )}
        </div>

        {!canViewKpi ? (
          <FinanceCapabilityState
            state="NOT_AVAILABLE"
            title="KPI Access Restricted"
            reason="Your role does not include finance.kpi.view permission. Contact your Finance administrator."
            data-testid="finance-kpi-no-access"
          />
        ) : error ? (
          <div
            data-testid="finance-kpi-error"
            className="flex items-center gap-2 p-4 rounded-lg bg-red-950/30 border border-red-900/40 text-red-300 text-sm"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        ) : !loading && kpi ? (
          <FK1KpiCards kpi={kpi} />
        ) : loading ? (
          <FK1KpiSkeleton />
        ) : null}
      </section>

      {/* FK2 Section — BLOCKED truthful state */}
      <section data-testid="finance-fk2-section">
        <h2 className="text-sm font-semibold text-slate-400 tracking-wider uppercase mb-4">
          Per-Program Finance KPI (FK2)
        </h2>
        <FinanceCapabilityState
          state="BLOCKED"
          title="Per-Program KPI"
          reason="FK2 requires an authoritative enrollment source (ENR1). Demo students are UNMAPPED — no canonical program enrollment exists. See ENR-SOD1 for the onboarding path."
          data-testid="finance-fk2-blocked"
        />
      </section>

      {/* Finance Workspace Navigation Tiles */}
      <section data-testid="finance-workspace-tiles">
        <h2 className="text-sm font-semibold text-slate-400 tracking-wider uppercase mb-4">
          Finance Workspaces
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <WorkspaceTile
            icon={Users}
            title="Fees & Student Accounts"
            description="View any student's account balance, charges, and payment history."
            to="/finance/accounts"
            live
            testId="finance-tile-student-accounts"
          />
          <WorkspaceTile
            icon={Wallet}
            title="Fee Setup"
            description="Define fee schedules, fee types, and published fee rules."
            comingSoon
            wave="FM2"
            testId="finance-tile-fee-setup"
          />
          <WorkspaceTile
            icon={BarChart2}
            title="Assessments"
            description="Run and confirm fee assessments against enrolled cohorts."
            blocked
            reason="Blocked: enrollment authority (ENR1) required."
            testId="finance-tile-assessments"
          />
          <WorkspaceTile
            icon={TrendingUp}
            title="Reports & Analytics"
            description="Period reports, collections summary, and Finance KPI exports."
            comingSoon
            wave="FM2"
            testId="finance-tile-reports"
          />
          <WorkspaceTile
            icon={Wallet}
            title="Payments & Reconciliation"
            description="Payment recording, bank reconciliation, and allocation management."
            comingSoon
            wave="FM3"
            testId="finance-tile-payments"
          />
          <WorkspaceTile
            icon={Wallet}
            title="Scholarships & Waivers"
            description="Award scholarships, approve waivers, and track reimbursements."
            comingSoon
            wave="FM4"
            testId="finance-tile-scholarships"
          />
        </div>
      </section>
    </div>
  );
}

// FK1 KPI Cards
function FK1KpiCards({ kpi }) {
  const isUnavailable = kpi?.fiscal_period_status === "UNAVAILABLE";
  const currency = "INR"; // FK1 frozen source — ISB is INR

  if (isUnavailable) {
    return (
      <FinanceCapabilityState
        state="NOT_CONFIGURED"
        title="No Current Fiscal Period"
        reason="No active fiscal year is configured. Finance Configuration → Fiscal Calendar must be set up."
        data-testid="finance-kpi-no-period"
      />
    );
  }

  const period = kpi?.period;

  return (
    <div className="space-y-3">
      {/* Period header */}
      {period && (
        <div
          data-testid="finance-kpi-period"
          className="flex items-center gap-2 text-xs text-slate-400"
        >
          <span className="px-2 py-0.5 rounded bg-emerald-900/40 text-emerald-300 font-semibold">
            FISCAL YEAR
          </span>
          <span>{period.label}</span>
          <span className="text-slate-600">
            {period.start_date} → {period.end_date}
          </span>
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          label="Fees Billed"
          amount={kpi?.fees_billed}
          currency={currency}
          testId="finance-kpi-fees-billed"
          variant="neutral"
        />
        <KpiCard
          label="Fees Collected"
          amount={kpi?.fees_collected}
          currency={currency}
          testId="finance-kpi-fees-collected"
          variant="paid"
        />
        <CollectionRateCard
          rate={kpi?.collection_rate}
          testId="finance-kpi-collection-rate"
        />
      </div>

      {/* NOT_CONNECTED KPIs */}
      {kpi?.not_connected_kpis?.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {kpi.not_connected_kpis.map((k) => (
            <FinanceCapabilityState
              key={k}
              state="NOT_AVAILABLE"
              title={k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              compact
              data-testid={`finance-kpi-not-connected-${k}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, amount, currency, testId, variant = "neutral" }) {
  return (
    <div
      data-testid={testId}
      className="p-4 rounded-lg bg-slate-900 border border-slate-800 space-y-1"
    >
      <p className="text-xs text-slate-500 font-medium">{label}</p>
      <MoneyAmount
        amount={amount}
        currency={currency}
        size="2xl"
        data-testid={`${testId}-amount`}
      />
    </div>
  );
}

function CollectionRateCard({ rate, testId }) {
  const displayRate =
    rate === null || rate === undefined ? null : `${rate.toFixed(2)}%`;

  return (
    <div
      data-testid={testId}
      className="p-4 rounded-lg bg-slate-900 border border-slate-800 space-y-1"
    >
      <p className="text-xs text-slate-500 font-medium">Collection Rate</p>
      {displayRate === null ? (
        <span className="text-2xl font-bold text-slate-500">—</span>
      ) : (
        <span
          data-testid={`${testId}-value`}
          className="text-2xl font-bold text-slate-100 tabular-nums"
        >
          {displayRate}
        </span>
      )}
    </div>
  );
}

function FK1KpiSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="p-4 rounded-lg bg-slate-900 border border-slate-800 animate-pulse space-y-2"
        >
          <div className="h-3 w-24 bg-slate-800 rounded" />
          <div className="h-8 w-32 bg-slate-700 rounded" />
        </div>
      ))}
    </div>
  );
}

function WorkspaceTile({ icon: Icon, title, description, to, live, comingSoon, blocked, reason, wave, testId }) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (live && to) navigate(to);
  };

  return (
    <button
      data-testid={testId}
      onClick={handleClick}
      disabled={!live}
      className={`text-left p-4 rounded-lg border transition-all space-y-2 w-full group ${
        live
          ? "bg-slate-900 border-slate-700 hover:border-blue-600 hover:bg-slate-800 cursor-pointer"
          : "bg-slate-900/50 border-slate-800/60 cursor-default opacity-70"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="p-2 rounded-md bg-slate-800 group-hover:bg-blue-900/30">
          <Icon className="h-4 w-4 text-slate-400 group-hover:text-blue-400" />
        </div>
        {live && <ArrowRight className="h-4 w-4 text-slate-600 group-hover:text-blue-400 mt-1" />}
        {comingSoon && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-900/40 text-blue-400 font-semibold">
            FM{wave ? wave.replace("FM", "") : "2"}
          </span>
        )}
        {blocked && (
          <Lock className="h-3.5 w-3.5 text-purple-500 mt-1" />
        )}
      </div>
      <div>
        <p className="text-sm font-medium text-slate-200">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        {blocked && reason && (
          <p className="text-xs text-purple-400/80 mt-1">{reason}</p>
        )}
      </div>
    </button>
  );
}
